/**
 * /eleve/dashboard — "Ma journée" style codevoiture.fr
 *
 * Sections :
 *  - Overline "Bonjour {prenom}" + titre Playfair "Ma journée"
 *  - Card "Mes prochains rendez-vous" (rouge)
 *  - Grille : Progression REMC (jauge + barres compet.) + Mon enseignant ECSR
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar, TrendingUp, GraduationCap, BookOpen, ArrowRight, Mail, Users,
  Film, Clock, CheckCircle2, XCircle, PlusCircle,
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import api from '../../api';

const REMC_LABELS = [
  { key: 'C1', label: 'Maîtrise du véhicule', total: 12 },
  { key: 'C2', label: 'Circulation', total: 8 },
  { key: 'C3', label: 'Conditions difficiles', total: 11 },
  { key: 'C4', label: 'Autonomie', total: 7 },
];

export default function EleveDashboardPage() {
  const { user } = useApp() || {};
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Shorts personnels — récupérés en parallèle du dashboard pour afficher
  // directement les 3 compteurs (en attente / publié / refusé) + CTA Proposer.
  const [myShorts, setMyShorts] = useState(null); // null = loading, [] = loaded empty

  useEffect(() => {
    document.title = 'Ma journée · Espace élève';
    (async () => {
      try {
        const d = await api.boEleveDashboard();
        setData(d);
      } catch (e) {
        if (e?.status === 401) navigate('/login');
      } finally {
        setLoading(false);
      }
    })();
    // Chargement indépendant des shorts (erreur silencieuse si l'élève n'a jamais proposé)
    (async () => {
      try {
        const list = await api.shortsMine();
        setMyShorts(Array.isArray(list) ? list : []);
      } catch (_) {
        setMyShorts([]);
      }
    })();
  }, [navigate]);

  const firstName = (user?.name || user?.email || 'toi').split(' ')[0].split('@')[0];
  const upcoming = data?.upcoming || [];
  const nextLesson = upcoming[0] || null;
  const teacher = data?.teacher || null;
  const compFreq = data?.competences_frequency || [];

  // Comp par préfixe C1/C2/C3/C4 (fallback si backend ne fournit pas)
  const compCounts = REMC_LABELS.map(({ key, label, total }) => {
    const done = compFreq.filter(c => (c.key || '').toUpperCase().startsWith(key)).reduce((s, c) => s + (c.count || 0), 0);
    return { key, label, done: Math.min(done, total), total };
  });
  const totalDone = compCounts.reduce((s, c) => s + c.done, 0);
  const totalCount = compCounts.reduce((s, c) => s + c.total, 0);
  const globalPct = totalCount ? Math.round((totalDone / totalCount) * 100) : 0;

  // Comptage par statut pour la carte "Mes Shorts" (en attente / publié / refusé)
  const shortsLoading = myShorts === null;
  const shortsPending = (myShorts || []).filter((s) => s.status === 'pending').length;
  const shortsPublished = (myShorts || []).filter((s) => s.status === 'published').length;
  const shortsRejected = (myShorts || []).filter((s) => s.status === 'rejected').length;
  const shortsTotal = shortsPending + shortsPublished + shortsRejected;

  return (
    <div className="max-w-5xl" data-testid="eleve-dashboard">
      {/* Overline + titre */}
      <div className="mb-6">
        <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2">
          Bonjour {firstName}
        </div>
        <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Ma journée
        </h1>
        <p className="text-sm text-white/60 mt-2 max-w-2xl">
          Le déroulé clair de ton parcours : tes prochains rendez-vous, ta formation, tes comptes-rendus puis ta progression.
        </p>
      </div>

      {/* Prochains RDV — cliquable → planning perso pour ajouter/voir */}
      <Link
        to="/eleve/planning"
        data-testid="next-rdv-card"
        className="block group rounded-2xl border border-rose-500/30 bg-gradient-to-br from-rose-500/[0.08] to-rose-500/[0.02] hover:from-rose-500/[0.14] hover:to-rose-500/[0.04] hover:border-rose-500/50 p-5 sm:p-6 mb-6 transition"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-rose-500/20 border border-rose-500/30 text-rose-400 flex items-center justify-center flex-shrink-0 group-hover:bg-rose-500/30 transition">
            <Calendar className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400 mb-1">Mes prochains rendez-vous</div>
            {loading ? (
              <div className="text-white/40 text-sm">Chargement…</div>
            ) : nextLesson ? (
              <div>
                <div className="text-white text-lg font-semibold" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {new Date(nextLesson.date + 'T' + (nextLesson.start_time || '00:00')).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
                <div className="text-white/60 text-sm mt-0.5">
                  {(nextLesson.start_time || '').slice(0, 5)} · {nextLesson.type || 'Conduite'} avec {nextLesson.teacher_name || 'ton moniteur'}
                </div>
                <div className="text-rose-300 text-[11px] font-semibold mt-2 inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                  Ouvrir mon planning <ArrowRight size={11} />
                </div>
              </div>
            ) : (
              <div>
                <div className="text-white text-lg font-semibold" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Aucune leçon planifiée
                </div>
                <div className="text-white/50 text-sm mt-0.5">
                  Clique ici pour ajouter ton prochain rendez-vous ou une révision.
                </div>
                <div className="text-rose-300 text-[11px] font-semibold mt-2 inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                  + Ajouter un rendez-vous <ArrowRight size={11} />
                </div>
              </div>
            )}
          </div>
          <div className="hidden sm:inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-white/15 text-white/75 group-hover:bg-white/5 group-hover:text-white text-xs font-semibold transition">
            Tout voir <ArrowRight size={12} />
          </div>
        </div>
      </Link>

      {/* ======== MES SHORTS — carte dédiée avec 3 statuts (en attente / publié / refusé) ========
          Visible immédiatement sous les RDV, car la user demande "la chose la plus importante
          avec les leçons et le livret". 3 pastilles cliquables filtrent /eleve/mes-shorts. */}
      <div
        className="rounded-2xl border border-rose-500/25 bg-gradient-to-br from-rose-500/[0.07] to-black/60 p-5 sm:p-6 mb-6"
        data-testid="eleve-dashboard-shorts-card"
      >
        <div className="flex items-start gap-3 mb-4 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-rose-500/20 border border-rose-500/30 text-rose-400 flex items-center justify-center flex-shrink-0">
            <Film className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400 mb-1">Mes Shorts communauté</div>
            <h3 className="text-xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {shortsLoading
                ? 'Chargement de mes propositions…'
                : shortsTotal === 0
                  ? 'Partage ta première vidéo'
                  : `${shortsTotal} proposition${shortsTotal > 1 ? 's' : ''} envoyée${shortsTotal > 1 ? 's' : ''}`}
            </h3>
            <p className="text-sm text-white/60 mt-1">
              Ton astuce moto peut aider d'autres élèves. Soumets une vidéo (TikTok, YouTube, Vimeo…) — relecture sous 48 h.
            </p>
          </div>
          <Link
            to="/shorts/proposer"
            data-testid="eleve-dashboard-shorts-cta-propose"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-sm shadow-lg shadow-rose-900/30 transition"
          >
            <PlusCircle className="w-4 h-4" /> Proposer un Short
          </Link>
        </div>

        {/* 3 pastilles cliquables — chacune ouvre /eleve/mes-shorts avec filtre statut */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <Link
            to="/eleve/mes-shorts?status=pending"
            data-testid="eleve-dashboard-shorts-tile-pending"
            className={`group rounded-xl border p-3 sm:p-4 transition text-left ${
              shortsPending > 0
                ? 'border-amber-500/40 bg-amber-500/[0.08] hover:bg-amber-500/[0.14] hover:border-amber-500/60'
                : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20'
            }`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <Clock className={`w-4 h-4 ${shortsPending > 0 ? 'text-amber-400' : 'text-white/40'}`} />
              <div className={`text-[9.5px] font-black uppercase tracking-widest ${shortsPending > 0 ? 'text-amber-300' : 'text-white/50'}`}>
                En attente
              </div>
            </div>
            <div className={`text-3xl sm:text-4xl font-black leading-none ${shortsPending > 0 ? 'text-amber-200' : 'text-white/70'}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {shortsLoading ? '—' : shortsPending}
            </div>
            <div className="text-[10.5px] text-white/50 mt-1.5 group-hover:text-white/70 transition">
              {shortsPending > 0 ? 'Modération en cours' : 'Rien en attente'}
            </div>
          </Link>

          <Link
            to="/eleve/mes-shorts?status=published"
            data-testid="eleve-dashboard-shorts-tile-published"
            className={`group rounded-xl border p-3 sm:p-4 transition text-left ${
              shortsPublished > 0
                ? 'border-emerald-500/40 bg-emerald-500/[0.08] hover:bg-emerald-500/[0.14] hover:border-emerald-500/60'
                : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20'
            }`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <CheckCircle2 className={`w-4 h-4 ${shortsPublished > 0 ? 'text-emerald-400' : 'text-white/40'}`} />
              <div className={`text-[9.5px] font-black uppercase tracking-widest ${shortsPublished > 0 ? 'text-emerald-300' : 'text-white/50'}`}>
                Publiés
              </div>
            </div>
            <div className={`text-3xl sm:text-4xl font-black leading-none ${shortsPublished > 0 ? 'text-emerald-200' : 'text-white/70'}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {shortsLoading ? '—' : shortsPublished}
            </div>
            <div className="text-[10.5px] text-white/50 mt-1.5 group-hover:text-white/70 transition">
              {shortsPublished > 0 ? 'En ligne, visibles' : 'Aucun publié'}
            </div>
          </Link>

          <Link
            to="/eleve/mes-shorts?status=rejected"
            data-testid="eleve-dashboard-shorts-tile-rejected"
            className={`group rounded-xl border p-3 sm:p-4 transition text-left ${
              shortsRejected > 0
                ? 'border-red-500/40 bg-red-500/[0.08] hover:bg-red-500/[0.14] hover:border-red-500/60'
                : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20'
            }`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <XCircle className={`w-4 h-4 ${shortsRejected > 0 ? 'text-red-400' : 'text-white/40'}`} />
              <div className={`text-[9.5px] font-black uppercase tracking-widest ${shortsRejected > 0 ? 'text-red-300' : 'text-white/50'}`}>
                Refusés
              </div>
            </div>
            <div className={`text-3xl sm:text-4xl font-black leading-none ${shortsRejected > 0 ? 'text-red-200' : 'text-white/70'}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {shortsLoading ? '—' : shortsRejected}
            </div>
            <div className="text-[10.5px] text-white/50 mt-1.5 group-hover:text-white/70 transition">
              {shortsRejected > 0 ? 'Voir le motif' : 'Aucun refus'}
            </div>
          </Link>
        </div>

        {/* Lien discret vers la vue détaillée */}
        <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
          <Link
            to="/eleve/mes-shorts"
            data-testid="eleve-dashboard-shorts-fullview"
            className="inline-flex items-center gap-1.5 text-[11.5px] font-black uppercase tracking-wider text-rose-300 hover:text-rose-200 transition"
          >
            Vue détaillée de mes shorts <ArrowRight size={12} />
          </Link>
          <Link
            to="/shorts"
            data-testid="eleve-dashboard-shorts-feed"
            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-white/60 hover:text-white/90 transition"
          >
            Explorer le fil Shorts →
          </Link>
        </div>
      </div>

      {/* Grille : Progression + Enseignant */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Progression REMC */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="progression-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-rose-500/20 border border-rose-500/30 text-rose-400 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400/80">Progression REMC</div>
              <div className="flex items-baseline gap-2 mt-1">
                <div className="text-2xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{totalDone}/{totalCount} validés</div>
              </div>
            </div>
            <div className="text-4xl sm:text-5xl font-black text-rose-500" style={{ fontFamily: "'Playfair Display', Georgia, serif" }} data-testid="progression-pct">
              {globalPct}<span className="text-xl align-top">%</span>
            </div>
          </div>

          {/* Barres par compétence */}
          <div className="space-y-3 mb-4">
            {compCounts.map(c => (
              <div key={c.key}>
                <div className="flex items-baseline justify-between text-sm mb-1">
                  <div className="text-white/80 font-semibold">{c.key} · {c.label}</div>
                  <div className="text-white/50 text-xs font-mono">{c.done}/{c.total}</div>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-rose-500 rounded-full transition-all" style={{ width: `${c.total ? (c.done / c.total) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>

          <Link to="/eleve/livret" data-testid="btn-continue-livret" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-sm shadow-lg shadow-rose-900/30 transition">
            <BookOpen className="w-4 h-4" /> Continuer mon livret <ArrowRight size={14} />
          </Link>
        </div>

        {/* Mon enseignant — cliquable → /eleve/mes-moniteurs pour ajouter/gérer */}
        <Link
          to="/eleve/mes-moniteurs"
          data-testid="teacher-card"
          className="block group rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 p-5 sm:p-6 transition"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-rose-500/20 border border-rose-500/30 text-rose-400 flex items-center justify-center flex-shrink-0 group-hover:bg-rose-500/30 transition">
              <GraduationCap className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400/80">Mon enseignant ECSR</div>
              {teacher ? (
                <>
                  <div className="text-xl font-black text-white mt-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{teacher.name}</div>
                  {teacher.email && (
                    <span className="text-sm text-white/60 inline-flex items-center gap-1.5 mt-0.5">
                      <Mail size={12} /> {teacher.email}
                    </span>
                  )}
                </>
              ) : (
                <div className="text-white/60 text-sm mt-1">Aucun enseignant assigné pour le moment.</div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] p-3 flex items-start gap-2 mb-3">
            <Users className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-emerald-200/90 leading-relaxed">
              Ton moniteur peut consulter et valider tes compétences REMC après chaque leçon.
            </div>
          </div>

          <div className="text-rose-300 text-[12px] font-semibold inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
            {teacher ? '+ Ajouter ou changer de moniteur' : '+ Ajouter mon moniteur / auto-école'} <ArrowRight size={11} />
          </div>
        </Link>
      </div>

      {/* Accueil si pas encore lié */}
      {data && !data.linked && (
        <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="eleve-not-linked">
          <div className="text-amber-300 font-black text-sm mb-1">Compte non lié à une fiche élève</div>
          <div className="text-white/70 text-sm">{data.message || 'Le secrétariat doit lier ton compte à ta fiche élève pour débloquer les fonctionnalités (planning, bilans, progression).'}</div>
        </div>
      )}
    </div>
  );
}
